import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Paper, Stack, Switch, Typography } from "@mui/material";
import { toast } from "react-toastify";

import Breadcrumb from "components/Atoms/SharedComponents/Breadcrumbs/Breadcrumb";
import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import { useThemePalette } from "common/hooks/theme_palette";
import setAuthToken from "services/modules/utils/handel_api";
import {
  getSystemConfigRequest,
  updateSystemConfigRequest,
} from "services/modules/systemConfig";

const PACKAGING_KEYS = [
  "packaging_enabled",
  "packaging_ready_enabled",
  "packaging_custom_enabled",
];

function PackagingSettingsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { oliveGreen } = useThemePalette();
  const token = localStorage.getItem("token");
  setAuthToken(token);

  const list = useSelector((state) => state.systemConfig.systemConfigs);
  const load = useSelector((state) => state.systemConfig.load);
  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile,
  );
  const permissions = useSelector(
    (state) =>
      state.authenticate.currentUserProfile.permissions.system_configurations,
  );
  const isSuperAdmin = currentUser?.privilege == "super_admin";
  const canUpdate = isSuperAdmin || permissions?.can_update;

  const [updatingKey, setUpdatingKey] = useState(null);

  const packagingConfigs = useMemo(() => {
    const byKey = {};
    list?.forEach((item) => {
      if (PACKAGING_KEYS.includes(item?.key)) {
        byKey[item.key] = item;
      }
    });
    return byKey;
  }, [list]);

  const fetchPackagingConfigs = () => {
    dispatch(
      getSystemConfigRequest({
        params: {
          page_size: 50,
          page_number: 1,
          keyword: "packaging",
          sort_by: "id",
        },
      }),
    );
  };

  useEffect(() => {
    fetchPackagingConfigs();
  }, []);

  useEffect(() => {
    if (!load?.loading) {
      setUpdatingKey(null);
    }
  }, [load?.loading]);

  const isEnabled = (key) => {
    const value = packagingConfigs[key]?.value;
    return value === true || value === "true";
  };

  const handleToggle = (key) => (event) => {
    const config = packagingConfigs[key];
    if (!config?.id || !canUpdate) return;

    const nextValue = event.target.checked ? "true" : "false";
    setUpdatingKey(key);

    dispatch(
      updateSystemConfigRequest({
        id: config.id,
        body: {
          system_configuration: {
            value: nextValue,
          },
        },
        action: () => {
          setUpdatingKey(null);
          toast.success(t("packaging_settings.updated"));
          fetchPackagingConfigs();
        },
      }),
    );
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: false,
      title: t("packaging_settings.title"),
    },
  ];

  const rows = [
    {
      key: "packaging_enabled",
      label: t("packaging_settings.packaging_enabled"),
      description: t("packaging_settings.packaging_enabled_desc"),
    },
    {
      key: "packaging_ready_enabled",
      label: t("packaging_settings.packaging_ready_enabled"),
      description: t("packaging_settings.packaging_ready_enabled_desc"),
    },
    {
      key: "packaging_custom_enabled",
      label: t("packaging_settings.packaging_custom_enabled"),
      description: t("packaging_settings.packaging_custom_enabled_desc"),
    },
  ];

  return (
    <>
      <Loader open={load?.loading && !updatingKey} />
      <Box>
        <Stack
          direction="row"
          alignSelf="start"
          justifyContent="start"
          gap={2}
          width="100%"
          mb={2}
        >
          <Breadcrumb data={breadcrumbs} />
        </Stack>

        <Typography fontSize="24px" fontWeight="600" mb={3}>
          {t("packaging_settings.title")}
        </Typography>

        <Stack gap={2} maxWidth="720px">
          {rows.map((row) => {
            const config = packagingConfigs[row.key];
            const checked = isEnabled(row.key);
            const disabled =
              !canUpdate || !config?.id || updatingKey === row.key;

            return (
              <Paper
                key={row.key}
                elevation={0}
                sx={{
                  p: 2.5,
                  border: "1px solid #E8E8E8",
                  borderRadius: "8px",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                >
                  <Box>
                    <Typography fontSize="18px" fontWeight="600" mb={0.5}>
                      {row.label}
                    </Typography>
                    <Typography fontSize="14px" color="#797979">
                      {row.description}
                    </Typography>
                    {!config?.id && (
                      <Typography fontSize="13px" color="#C62828" mt={1}>
                        {t("packaging_settings.config_missing")}
                      </Typography>
                    )}
                  </Box>
                  <Switch
                    checked={checked}
                    onChange={handleToggle(row.key)}
                    disabled={disabled}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: oliveGreen,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: oliveGreen,
                      },
                    }}
                  />
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </Box>
    </>
  );
}

export default PackagingSettingsPage;
